import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { PrimaryButton } from '@/components/common/Buttons';
import { RichHeading } from '@/components/common/Heading';
import { HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { SliderV2 } from '@/components/common/SliderCommon/SliderV2';
import { Tag } from '@/components/common/Tag';
import { Section } from '@/components/layout/Section';

import { TestimonialCard } from './TestimonialCard';

import type { SanityTestimonialsCarouselType } from '@/types/sanity';

export interface TestimonialsCarouselProps
  extends SanityTestimonialsCarouselType {}

const TestimonialsCarousel = ({
  tag,
  heading,
  headline,
  testimonials,
}: TestimonialsCarouselProps) => {
  const sliderSettings = {
    dots: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    rows: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 640,
        settings: {
          rows: 1,
          slidesPerRow: 1,
          centerMode: true,
          centerPadding: '12px',
        },
      },
    ],
  };

  return (
    <Section backgroundColour="--background-subtle">
      <HeadingLevel>
        <TestimonialsContainer>
          <HeadingSection>
            {tag?.tagText && (
              <Tag
                bgColour={tag?.tagColour}
                margin="0 0 var(--spacing-xxx-small) 0"
              >
                {tag.tagText}
              </Tag>
            )}
            <Paragraph size="body-md">{headline}</Paragraph>
            <RichHeading heading={heading} size="headline-lg" />
          </HeadingSection>
          <SliderWrapper>
            <SliderV2
              settings={sliderSettings}
              slideItems={testimonials}
              componentClassName="tracking-testimonials-carousel"
            >
              {testimonials.map((testimonialSlide) => (
                <TestimonialCardsWrapper key={testimonialSlide._key}>
                  <TestimonialCards>
                    {testimonialSlide.testimonialQuotes.map((quote) => (
                      <TestimonialCard
                        key={quote._key}
                        logo={testimonialSlide.logo}
                        company={testimonialSlide.company}
                        {...quote}
                      />
                    ))}
                  </TestimonialCards>
                  {!!testimonialSlide.ctaButton?.buttonLink && (
                    <CTAContainer>
                      <StyledButton
                        iconPosition="right"
                        componentTheme="light"
                        href={testimonialSlide.ctaButton.buttonLink}
                        aria-label={
                          testimonialSlide.ctaButton.buttonAccessibleLabel
                        }
                        className={
                          testimonialSlide.ctaButton.trackingTag?.blockName &&
                          `tracking-${testimonialSlide.ctaButton.trackingTag.blockName}__${testimonialSlide.ctaButton.trackingTag.blockAction}`
                        }
                      >
                        {testimonialSlide.ctaButton.buttonText}
                      </StyledButton>
                    </CTAContainer>
                  )}
                </TestimonialCardsWrapper>
              ))}
            </SliderV2>
          </SliderWrapper>
        </TestimonialsContainer>
      </HeadingLevel>
    </Section>
  );
};

const TestimonialCardsWrapper = styled.div``;

const HeadingSection = styled.div`
  text-align: center;
  margin-bottom: calc(var(--spacing-500) + var(--spacing-xx-small));
  padding: var(--spacing-0) var(--section-gutter);

  p {
    margin-bottom: var(--spacing-x-small);
    color: var(--text-strong);
  }

  ${screen.md} {
    margin-bottom: calc(var(--spacing-800) + var(--spacing-xx-small));
  }
`;

const SliderWrapper = styled.div`
  position: relative;
  max-width: 640px;
  margin: var(--spacing-0) auto;

  > svg {
    position: absolute;
    height: 200px;
    width: auto;

    &:first-of-type {
      transform: rotate(90deg);
      top: -1.6rem;
      left: -1.4rem;
    }
    &:not(:first-of-type) {
      bottom: 9.6rem;
      right: -1rem;
    }
  }

  ${screen.md} {
    max-width: 90%;

    > svg {
      &:first-of-type {
        transform: rotate(0deg);
        top: unset;
        bottom: calc(var(--spacing-1000) + 86px);
      }

      &:not(:first-of-type) {
        transform: rotate(325deg);
        bottom: unset;
        top: -1.5rem;
        right: -1rem;
      }
    }
  }

  ${screen.lg} {
    max-width: 1060px;
  }
`;

const TestimonialsContainer = styled.div`
  padding: var(--spacing-500) 0 var(--spacing-small);
  position: relative;

  a {
    margin: var(--spacing-300) auto var(--spacing-0) auto;
  }

  ${screen.md} {
    a {
      margin: var(--spacing-300) auto var(--spacing-0) auto;
    }
  }
`;
const CTAContainer = styled.div`
  display: flex;
`;
const TestimonialCards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0px;

  ${screen.md} {
    grid-template-columns: repeat(auto-fit, minmax(calc(25% - 1rem), 1fr));
    gap: var(--spacing-400);
  }
  ${screen.lg} {
    gap: var(--spacing-500);
  }
`;

const StyledButton = styled(PrimaryButton)``;

export { TestimonialsCarousel };
